import { useContext, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import uniq from "lodash/uniq";
import AlertMessage from "app/pages/.shared/AlertMessage";
import { ALERT_TYPE } from "app/constants";
import PaymentPassengersFieldset from "app/pages/Booking/Payment/PaymentPassengersSection/PaymentPassengersFieldset";
import AppGlobalsContext from "app/AppGlobalsContext";
import PropTypes from "prop-types";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import IconPaymentDisclaimer from "app/pages/.shared/static/icons/IconPaymentDisclaimer";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const PaymentPassengersSection = ({
	passengersErrorList = [],
	bookStatus,
	productUri,
	dateOfBirthRequired,
	showFrequentTravellerCardInput,
	isChildrenAgeRequired,
	passengers = [],
	error,
	isPassengersError,
	showInsurancesSection,
	onBlur = () => {},
	warningMessage = false,
}) => {
	const { resolution } = useContext(AppGlobalsContext);

	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	useEffect(() => {
		if (isElementOnScreen) {
			track("payment_view_information");
		}
	}, [isElementOnScreen]);

	return (
		<>
			<Typography
				variant={TYPOGRAPHY_VARIANTS.XL3}
				variantMD={TYPOGRAPHY_VARIANTS.XL4}
				isBold
				className="payment__title"
				component="h2"
			>
				<FormattedMessage id="payment.passengers.title" />
			</Typography>
			<div className="payment__content">
				<div className="payment__disclaimer">
					<IconPaymentDisclaimer />
					<div className="payment__disclaimer-content">
						<FormattedMessage id="payment.disclamer.1" tagName="div" />
						<FormattedMessage id="payment.disclamer.2" tagName="div" />
					</div>
				</div>
				{uniq(passengersErrorList).length > 0 && (
					<AlertMessage
						alertType={ALERT_TYPE.ERROR}
						message={<FormattedMessage id="error.booking.payment.fields.required" />}
						errors={uniq(passengersErrorList)}
					/>
				)}
				{warningMessage && (
					<AlertMessage
						alertType={ALERT_TYPE.INFO}
						message={<FormattedMessage id="general.warning.adult.destination.usa" />}
					/>
				)}
				<div className="payment__passengers">
					<PaymentPassengersFieldset
						bookStatus={bookStatus}
						resolution={resolution}
						productUri={productUri}
						dateOfBirthRequired={dateOfBirthRequired}
						showFrequentTravellerCardInput={showFrequentTravellerCardInput}
						isChildrenAgeRequired={isChildrenAgeRequired}
						onBlur={onBlur}
						passengers={passengers}
						error={error}
						showError={error && isPassengersError}
					/>
				</div>
				{!showInsurancesSection && (
					<div className="payment__insurance-note">
						<FormattedMessage id="payment.passengers.insurance.note" />
					</div>
				)}
			</div>
		</>
	);
};

PaymentPassengersSection.propTypes = {
	passengersErrorList: PropTypes.array,
	bookStatus: PropTypes.string,
	productUri: PropTypes.string,
	dateOfBirthRequired: PropTypes.bool,
	showFrequentTravellerCardInput: PropTypes.bool,
	isChildrenAgeRequired: PropTypes.bool,
	passengers: PropTypes.array,
	error: PropTypes.object,
	isPassengersError: PropTypes.bool,
	showInsurancesSection: PropTypes.bool,
	onBlur: PropTypes.func,
	warningMessage: PropTypes.bool,
};

export default PaymentPassengersSection;
